import ky, { type KyInstance } from 'ky'
import { createSignal } from 'solid-js'
import { native, type UserInfo } from '@forest/native-services'
import { shouldMockAPI } from './params'

const mode = import.meta.env.MODE as 'production' | 'preview' | 'dev'

const userInfo = {
  _ready: null as null | Promise<UserInfo>,
  get ready() {
    this._ready ||= (async function getUserInfo() {
      const result = await native.user.getUserInfo()
      if (!result.success) throw new Error('Failed to get user info')
      if (!result.data) throw new Error('User info is empty')
      return result.data
    })()
    return this._ready
  },
  set ready(value: Promise<UserInfo>) {
    this._ready = value
  },
}

if (import.meta.env.DEV && shouldMockAPI) {
  userInfo.ready = new Promise((r) => {
    setTimeout(() => {
      r({
        user_type: 'legacy',
        user_id: 1,
        server: 'china',
        token: 'mock_token',
      })
    }, 1000)
  })
}

async function refreshToken() {
  const result = await native.user.refreshToken()
  if (!result.success) throw new Error('Failed to refresh token')
  userInfo.ready = Promise.resolve(result.data)
}

const base = ky.create({
  hooks: {
    beforeRequest: [
      async (options) => {
        const { user_type: userType, token } = await userInfo.ready
        if (userType === 'legacy') {
          options.headers.set('X-Auth-Token', token)
          options.headers.set('Remember-Token', token)
        } else {
          options.headers.set('Authorization', `Bearer ${token}`)
        }
      },
    ],
    afterResponse: [
      async (request, _, response) => {
        if (response.status === 401 || response.status === 403) {
          await refreshToken()
          const { token } = await userInfo.ready
          request.headers.set('Authorization', `Bearer ${token}`)
          return await ky(request)
        }
        return response
      },
    ],
  },
})

const serviceServerUrls: Record<UserInfo['server'], string> = {
  dev: 'https://forest-api.service.develop-cluster.seekrtech.com',
  staging: 'https://forest-api-staging.seekrtech.com',
  global: 'https://forest-api.seekrtech.com',
  china: 'https://forest-api.upwardsware.com',
}

let _resolvedInfo: UserInfo | null = null
const [_server, _setServer] = createSignal<UserInfo['server']>('global')

userInfo.ready.then((info) => {
  _resolvedInfo = info
  _setServer(info.server)
})

export const getServer = _server

function getServiceUrl(server: UserInfo['server']) {
  if (import.meta.env.DEV) return location.origin + '/service'
  if (mode === 'production') return serviceServerUrls[server]
  if (mode === 'dev') return serviceServerUrls.dev
  return serviceServerUrls.staging
}

let _serviceClient: KyInstance | null = null

export async function getServiceClient() {
  if (!_serviceClient) {
    const info = await userInfo.ready
    _resolvedInfo = info
    _setServer(info.server)
    _serviceClient = base.extend({
      prefixUrl: getServiceUrl(info.server),
      timeout: 60_000,
    })
  }
  return { client: _serviceClient, userId: _resolvedInfo!.user_id }
}
